import React, { createContext, useContext, useMemo, useCallback } from 'react'
import { omitBy, isUndefined } from '@golden/utils'
import { getTime, startOfYear, startOfDay, endOfDay, subYears } from 'date-fns'
import MuiButton from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { GashLogQuery, GashTransactionType, PaginationReq } from '@golden/gdk-admin'
import { SearchToRequestFunc, InitialFormFunc, useRequestFromSearch, useChangeUrlSubmit } from '../../../../utils/default/ComplexFlowHook'
import { guaranteeNotUndefined, parseInt, guaranteeBeOneOf, getTimeFromDateInputValue, getValueFromChangeEvent, pipe, convertEmptyToUndefined, guaranteeBetween } from '../../../../utils/default/FormHelper'
import DateInputBase, { DateInputValue } from '../../../default/form/DateInput'
import { ValueGetter, createDefaultFormState, FormValidation } from '../../../../utils/default/FormHook'
import useT from '../../../../i18ns/admin/useT'
import DropDown, { PropTypes as DropDownProps } from '../../../default/form/DropDown'
import { setToday, setYesterday, setThisMonth, setThisWeek, setLastMonth, setLastWeek, createShouldDisableDate, createShouldDisableFuture, getCorrectedDateRange } from '@golden/shared'
import { useCommonStyles } from '../../../../utils/admin/StyleHook'
import FormStateProvider from '../../../default/form/FormStateProvider'
import useGDKStore from '../../../../providers/admin/gdk/useGDKStore'
import allRoute from '../../route/route'
import RequiredText from '../../../default/form/RequiredText'
import FormSubmitButton from '../../../default/form/FormSubmitButton'
import FormField from '../../../default/form/FormField'
import MuiTextField, { TextFieldProps } from '@material-ui/core/TextField'
import gashTransactionTypeName from '../../../../constants/admin/gashTransactionTypeName'
import { createValidateStartAtWithEndAt } from '../../../../utils/default/Validator'

export type GashLogRequest = PaginationReq & GashLogQuery

export interface GashLogFormType {
  time: DateInputValue
  action: GashTransactionType | '--'
  account: string
  transaction_number: string
  memo: string
}

export const initialForm: InitialFormFunc<GashLogFormType> = (defaultForm) => ({
  time: {
    start: startOfDay(new Date()),
    end: endOfDay(new Date())
  },
  action: '--',
  account: '',
  transaction_number: '',
  memo: '',
  ...defaultForm
})

const getValueFromEvent: ValueGetter<GashLogFormType> = {
  time: getTimeFromDateInputValue,
  action: getValueFromChangeEvent,
  account: getValueFromChangeEvent,
  transaction_number: getValueFromChangeEvent,
  memo: getValueFromChangeEvent
}

const formToRequest = (form: GashLogFormType): GashLogQuery => {
  const converted = {
    action: form.action === '--' ? undefined : form.action,
    start_at: form.time.start === null ? (form.time.end === null ? getTime(startOfDay(new Date())) : getTime(startOfDay(form.time.end))) : getTime(form.time.start),
    end_at: form.time.end === null ? getTime(endOfDay(new Date())) : getTime(form.time.end),
    account: convertEmptyToUndefined(form.account),
    transaction_number: convertEmptyToUndefined(form.transaction_number),
    memo: convertEmptyToUndefined(form.memo),
    page: 1
  } as GashLogQuery
  return omitBy(converted, isUndefined) as GashLogQuery
}

export const searchToRequest: SearchToRequestFunc<GashLogRequest> = (search) => {
  const converted = {
    ...search,
    page: pipe(
      guaranteeNotUndefined,
      parseInt,
      (value) => guaranteeBetween(value, 1, Number.MAX_SAFE_INTEGER)
    )(search.page),
    action: search.action
      ? pipe(
        (value) => guaranteeNotUndefined(value as string),
        (value) => guaranteeBeOneOf(value, Object.values(GashTransactionType))
      )(search.action)
      : undefined,
    start_at: pipe(
      (value) => guaranteeNotUndefined(value as string),
      parseInt,
      (value) => guaranteeBetween(value, 1, Number.MAX_SAFE_INTEGER)
    )(search.start_at),
    end_at: pipe(
      (value) => guaranteeNotUndefined(value as string),
      parseInt,
      (value) => guaranteeBetween(value, 1, Number.MAX_SAFE_INTEGER)
    )(search.end_at),
    account: search.account,
    transaction_number: search.transaction_number
  } as GashLogRequest
  return omitBy(converted, isUndefined) as GashLogRequest
}

const FormContext = createContext(createDefaultFormState(initialForm()))

const Button = React.memo(MuiButton)

const DateInput: React.FC = React.memo(() => {
  const classes = useCommonStyles()
  const { t } = useT()
  const { value, handleChange } = useContext(FormContext)
  const {
    shouldDisableStartDate,
    shouldDisableEndDate
  } = useMemo(() => createShouldDisableFuture(), [value.time])
  const tools = useMemo(() => {
    return [
      {
        label: t('common.today'),
        change: setToday
      },
      {
        label: t('common.yesterday'),
        change: setYesterday
      },
      {
        label: t('common.thisMonth'),
        change: setThisMonth
      },
      {
        label: t('common.lastMonth'),
        change: setLastMonth
      }
    ]
  }, [t])
  const startOption = useMemo(() => ({
    label: t('common.beginAt'),
    shouldDisableDate: shouldDisableStartDate
  }), [shouldDisableStartDate, t])

  const endOption = useMemo(() => ({
    label: t('common.endAt'),
    shouldDisableDate: shouldDisableEndDate
  }), [shouldDisableEndDate, t])

  const dateClasses = useMemo(() => ({
    button: classes.pinkGradualButton
  }), [classes.pinkGradualButton])

  const onChange = useCallback((value) => {
    handleChange('time')(getCorrectedDateRange(value))
  }, [])

  return (
    <DateInputBase
      value={value.time}
      onChange={onChange}
      start={startOption}
      end={endOption}
      tools={tools}
      classes={dateClasses}
    />
  )
})
const TextField = React.memo(MuiTextField)

const PlayerReportGashLogForm: React.FC = () => {
  const classes = useCommonStyles()
  const { t } = useT()
  const request = useRequestFromSearch({ searchToRequest })
  const defaultForm = useMemo(() => {
    if (request === undefined) {
      return initialForm()
    }
    return initialForm({
      ...request,
      time: {
        start: request.start_at ? new Date(request.start_at) : null,
        end: request.end_at ? new Date(request.end_at) : null
      }
    })
  }, [request])

  const handleSubmit = useChangeUrlSubmit({
    toAddNowTimestamp: true,
    formToRequest,
    encodePath: allRoute.playerReportGashLog.encodePath
  })

  const actionOptions = useMemo(() => {
    return [{ name: t('common.all'), value: '--' }].concat(Object.keys(gashTransactionTypeName)
      .filter((type => type !== GashTransactionType.SYSTEM_DEPOSIT))
      .map((type) => ({ value: type, name: t(gashTransactionTypeName[type as GashTransactionType]) })))
  }, [t])

  const validation = useMemo(() => ({
    time: [],
    action: [],
    account: [],
    transaction_number: [],
    memo: []
  } as FormValidation<GashLogFormType>), [])

  return (
    <FormStateProvider
      context={FormContext}
      defaultValue={defaultForm}
      onSubmit={handleSubmit}
      validation={validation}
      getValueFromEvent={getValueFromEvent}
    >
      <Paper>
        <Box padding={4}>
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <Box
                paddingY={1.25}
                paddingX={2}
                className={classes.pinkTitleBar}
              >
                <Typography variant="h5">
                  {t('page.gashLog')}
                </Typography>
              </Box>
            </Grid>
            <Grid item>
              <DateInput />
            </Grid>
            <Grid item>
              <Grid container direction="row" wrap="wrap" spacing={2}>
                <Grid item xs={12} md={6} lg={3}>
                  <FormField<GashLogFormType, DropDownProps>
                    context={FormContext}
                    component={DropDown}
                    name="action"
                    options={actionOptions}
                    label={t('common.transactionType')}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <FormField<GashLogFormType, TextFieldProps>
                    context={FormContext}
                    component={TextField}
                    name="account"
                    label={t('common.playerAccount')}
                    placeholder={t('placeholder.inputPlayerAccount')}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <FormField<GashLogFormType, TextFieldProps>
                    context={FormContext}
                    component={TextField}
                    name="transaction_number"
                    label={t('common.serialNumber')}
                    placeholder={t('placeholder.inputSerialNumber')}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <FormField<GashLogFormType, TextFieldProps>
                    context={FormContext}
                    component={TextField}
                    name="memo"
                    label={t('common.memo')}
                    placeholder={t('placeholder.inputMemo')}
                    multiline
                    maxRows={4}
                    fullWidth
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                        event.preventDefault()
                        event.currentTarget.closest('form')?.requestSubmit()
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <RequiredText />
            </Grid>
            <Grid item>
              <Grid container justifyContent="flex-end">
                <Grid item>
                  <FormSubmitButton
                    component={Button}
                    context={FormContext}
                    type="submit"
                    className={classes.purpleGradualButton}
                  >
                    {t('common.search')}
                  </FormSubmitButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </FormStateProvider>
  )
}

export default React.memo(PlayerReportGashLogForm)
